import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getRouterLinkUrlById, Interaction } from '@lsq-frontend/common-ui';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import {
    atlas,
    REPORTS_CHECK_NUMBER_RESULTS,
    REPORTS_INVOICE_NUMBER_RESULTS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { IGlobalSearch } from './global-search';

@Injectable()
export class GlobalSearchService {
    private _keepSearchOpenWhiteList: RegExp[] = [
        /\/check-number-results/,
        /\/invoice-number-results/
    ];
    protected _isExpandedSubject = new BehaviorSubject<boolean>(false);
    protected _rawSearchSubject = new BehaviorSubject<IGlobalSearch>(undefined);
    protected _searchSubject = new ReplaySubject<IGlobalSearch | null>(1);
    protected _searchClearedSubject = new Subject<any>();
    protected _minSearchLengthSubject = new BehaviorSubject<number>(
        Interaction.MinTextSearchLength
    );
    public isExpanded$: Observable<boolean>;
    public minSearchLength$: Observable<number>;
    public search$: Observable<IGlobalSearch | null>;
    public invoiceNumberSearch$: Observable<string | null>;
    public checkNumberSearch$: Observable<string | null>;
    public searchCleared$: Observable<any>;
    public isActive$: Observable<boolean>;
    public urlWhenLastSearchOccurred: string;

    constructor(private _router: Router) {
        this._init();

        // Configure.
        this._minSearchLengthSubject.next(Interaction.MinNumberSearchLength);

        // Process user input.
        this._rawSearchSubject
            .pipe(withLatestFrom(this.isActive$))
            .subscribe(([search, isActive]) => {
                if (isActive) {
                    this._searchSubject.next(search);
                } else {
                    this._searchSubject.next(null);
                }
            });

        this.invoiceNumberSearch$ = this.search$.pipe(
            map((search) => (search && search.invoiceNumber ? search.invoiceNumber : null))
        );

        this.checkNumberSearch$ = this.search$.pipe(
            map((search) => (search && search.checkNumber ? search.checkNumber : null))
        );

        this.invoiceNumberSearch$.subscribe((invoiceNumberSearch) => {
            if (invoiceNumberSearch) {
                this.recordUrlWhenLastSearchOccurred();
                return this._router.navigateByUrl(
                    getRouterLinkUrlById(atlas, REPORTS_INVOICE_NUMBER_RESULTS)
                );
            }
        });

        this.checkNumberSearch$.subscribe((checkNumberSearch) => {
            if (!this._isSearchingCheckNumbers() && checkNumberSearch) {
                this.recordUrlWhenLastSearchOccurred();
                return this._router.navigateByUrl(
                    getRouterLinkUrlById(atlas, REPORTS_CHECK_NUMBER_RESULTS)
                );
            }
        });

        this.isExpanded$.pipe(filter((isExpanded) => !isExpanded)).subscribe(() => {
            this.stopSearching();
        });
    }

    private _init(): void {
        this.isExpanded$ = this._isExpandedSubject.asObservable();
        this.minSearchLength$ = this._minSearchLengthSubject.asObservable();
        this.search$ = this._searchSubject.asObservable();

        this.isActive$ = combineLatest(this._hasValidSearchStream(), this._isExpandedSubject).pipe(
            map(([hasValidSearch, isExpanded]) => isExpanded && hasValidSearch)
        );

        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (
                    this._keepSearchOpenWhiteList.every(
                        (whiteListedPattern) =>
                            !this._router.url.match(new RegExp(whiteListedPattern, 'gi'))
                    )
                ) {
                    this.collapse();
                }
            });
    }

    public search(globalSearch: IGlobalSearch): void {
        this._rawSearchSubject.next(globalSearch);
    }

    public expand(): void {
        this._isExpandedSubject.next(true);
    }

    public collapse(): void {
        this._isExpandedSubject.next(false);
    }

    /** Upon exiting the global search, go back to where the user left off. */
    public stopSearching(): void {
        this._rawSearchSubject.next(null);
        if (this._isSearchingInvoiceNumbers() || this._isSearchingCheckNumbers()) {
            this._router.navigateByUrl(this.urlWhenLastSearchOccurred);
        }
    }

    public recordUrlWhenLastSearchOccurred(): void {
        if (!this._isSearchingInvoiceNumbers() && !this._isSearchingCheckNumbers()) {
            this.urlWhenLastSearchOccurred = this._router.url;
        }
    }

    private _hasValidSearchStream(): Observable<boolean> {
        return this._rawSearchSubject.pipe(
            withLatestFrom(this._minSearchLengthSubject),
            map(
                ([rawSearch, minLength]) =>
                    !!rawSearch &&
                    Object.keys(rawSearch)
                        .map((searchKey) => rawSearch[searchKey])
                        .some(
                            (searchValue) =>
                                typeof searchValue === 'string' && searchValue.length >= minLength
                        )
            )
        );
    }

    private _isSearchingCheckNumbers(): boolean {
        return this._router.isActive(
            getRouterLinkUrlById(atlas, REPORTS_CHECK_NUMBER_RESULTS),
            false
        );
    }

    private _isSearchingInvoiceNumbers(): boolean {
        return this._router.isActive(
            getRouterLinkUrlById(atlas, REPORTS_INVOICE_NUMBER_RESULTS),
            false
        );
    }
}
